import { useEffect, useCallback } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { firestore } from '../firebase';
import { broadcastNotificationStatusChange } from '../util/notificationEvents';

/**
 * Custom hook for managing notification read/unread status
 * Extracted from interaction.js for reuse in mobile and web components
 */
export const useNotificationStatus = () => {
  
  // Function to mark followers as read in Firestore
  const markFollowersAsReadInFirestore = useCallback(async (followers) => {
    try {
      const unreadFollowers = followers.filter(follower => follower.status === 'unread');
      
      if (unreadFollowers.length === 0) return;
      
      const updatePromises = unreadFollowers.map(follower => {
        const docRef = doc(firestore, "notifications", follower.id);
        return updateDoc(docRef, {
          status: 'read'
        });
      });
      
      await Promise.all(updatePromises);
      
    } catch (err) {
      console.error("Error updating follower status in Firestore:", err);
    }
  }, []);

  // Function to mark comments as read in Firestore
  const markCommentsAsReadInFirestore = useCallback(async (comments) => {
    try {
      const unreadComments = comments.filter(comment => comment.status === 'unread');
      
      if (unreadComments.length === 0) return;
      
      const updatePromises = unreadComments.map(comment => {
        const docRef = doc(firestore, "notifications", comment.id);
        return updateDoc(docRef, {
          status: 'read'
        });
      });
      
      await Promise.all(updatePromises);
      
    } catch (err) {
      console.error("Error updating comment status in Firestore:", err);
    }
  }, []);

  // Function to mark like notifications as read in Firestore
  const markLikesAsReadInFirestore = useCallback(async (likesByGameId) => {
    try {
      if (!likesByGameId) return;
      
      const allLikes = Object.values(likesByGameId)
        .flat()
        .filter(like => like.status === 'unread');
      
      if (allLikes.length === 0) return;
      
      const updatePromises = allLikes.map(like => {
        const docRef = doc(firestore, "notifications", like.id);
        return updateDoc(docRef, {
          status: 'read'
        });
      });
      
      await Promise.all(updatePromises);
      
    } catch (err) {
      console.error("Error updating like status in Firestore:", err);
    }
  }, []);

  // Function to mark specific notifications as read
  const markNotificationsAsRead = useCallback(async (notificationIds) => {
    try {
      if (!notificationIds || notificationIds.length === 0) return;
      
      const updatePromises = notificationIds.map(id => {
        const docRef = doc(firestore, "notifications", id);
        return updateDoc(docRef, {
          status: 'read'
        });
      });
      
      await Promise.all(updatePromises);
      
    } catch (err) {
      console.error("Error marking notifications as read:", err);
    }
  }, []);

  // Helper function to check if there are any unread notifications
  const hasUnreadNotifications = useCallback((followers, comments, likesByGameId) => {
    // Check followers
    if (followers && followers.some(follower => follower.status === 'unread')) {
      return true;
    }
    
    // Check comments
    if (comments && comments.some(comment => comment.status === 'unread')) {
      return true;
    }
    
    // Check likes
    if (likesByGameId) {
      const allLikes = Object.values(likesByGameId).flat();
      if (allLikes.some(like => like.status === 'unread')) {
        return true;
      }
    }
    
    return false;
  }, []);

  // Auto-mark functions for different scenarios
  const handleTabSwitch = useCallback(async (fromTab, toTab, followers, comments, likesByGameId) => {
    // If leaving the follows tab, mark all followers as read
    if (fromTab === 'follows' && toTab !== 'follows') {
      await markFollowersAsReadInFirestore(followers);
    }
    
    // If leaving the comments tab, mark all comments and likes as read
    if (fromTab === 'comments' && toTab !== 'comments') {
      await markCommentsAsReadInFirestore(comments);
      await markLikesAsReadInFirestore(likesByGameId);
    }
    
    // If entering the comments tab, mark likes as read (since they'll be viewed)
    if (toTab === 'comments' && fromTab !== 'comments') {
      await markLikesAsReadInFirestore(likesByGameId);
    }
  }, [markFollowersAsReadInFirestore, markCommentsAsReadInFirestore, markLikesAsReadInFirestore]);

  // Function to handle page visibility changes (mobile background/foreground)
  const handleVisibilityChange = useCallback(async (activeTab, followers, comments, likesByGameId) => {
    if (document.visibilityState === 'hidden') {
      if (activeTab === 'follows') {
        await markFollowersAsReadInFirestore(followers);
      } else if (activeTab === 'comments') {
        await markCommentsAsReadInFirestore(comments);
        await markLikesAsReadInFirestore(likesByGameId);
      }
    }
  }, [markFollowersAsReadInFirestore, markCommentsAsReadInFirestore, markLikesAsReadInFirestore]);

  // Function to handle component unmount
  const handleUnmount = useCallback(async (activeTab, followers, comments, likesByGameId) => {
    if (activeTab === 'follows') {
      await markFollowersAsReadInFirestore(followers);
    } else if (activeTab === 'comments') {
      await markCommentsAsReadInFirestore(comments);
      await markLikesAsReadInFirestore(likesByGameId);
    }
  }, [markFollowersAsReadInFirestore, markCommentsAsReadInFirestore, markLikesAsReadInFirestore]);

  // Hook to set up visibility change listeners
  const useVisibilityChangeListener = (activeTab, followers, comments, likesByGameId) => {
    useEffect(() => {
      const handleVisibilityChangeEvent = () => {
        handleVisibilityChange(activeTab, followers, comments, likesByGameId);
      };
      
      const handleBeforeUnload = () => {
        handleUnmount(activeTab, followers, comments, likesByGameId);
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChangeEvent);
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChangeEvent);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        
        // Also mark as read when unmounting component
        handleUnmount(activeTab, followers, comments, likesByGameId);
      };
    }, [activeTab, followers, comments, likesByGameId]);
  };

  // Local state update functions
  const markFollowersAsReadLocally = useCallback((followers, setFollowers) => {
    const updatedFollowers = followers.map(follower => ({
      ...follower,
      status: 'read'
    }));
    setFollowers(updatedFollowers);
    return updatedFollowers;
  }, []);

  const markCommentsAsReadLocally = useCallback((comments, setComments) => {
    const updatedComments = comments.map(comment => ({
      ...comment,
      status: 'read'
    }));
    setComments(updatedComments);
    return updatedComments;
  }, []);

  const markLikesAsReadLocally = useCallback((likesByGameId, setNotifications) => {
    const updatedLikesByGameId = { ...likesByGameId };
    Object.keys(updatedLikesByGameId).forEach(gameId => {
      updatedLikesByGameId[gameId] = updatedLikesByGameId[gameId].map(like => ({
        ...like,
        status: 'read'
      }));
    });
    
    setNotifications(prev => ({
      ...prev,
      likesByGameId: updatedLikesByGameId
    }));
    
    return updatedLikesByGameId;
  }, []);

  return {
    // Firestore update functions
    markFollowersAsReadInFirestore,
    markCommentsAsReadInFirestore,
    markLikesAsReadInFirestore,
    markNotificationsAsRead,
    
    // Event handlers
    handleTabSwitch,
    handleVisibilityChange,
    handleUnmount,
    
    // Local state update functions
    markFollowersAsReadLocally,
    markCommentsAsReadLocally,
    markLikesAsReadLocally,
    
    // Helper functions
    hasUnreadNotifications,
    
    // Hook for visibility listeners
    useVisibilityChangeListener
  };
};